/**
 * 業務マスタを案件に適用するミューテーション（クイックチップ / 行内検索の共通処理）
 * - usage_count を加算するため必ず workItemMasterApi.apply を経由する
 * - 単一明細マスタで金額を編集した場合のみ、作成された行を 1 回だけ更新する
 *
 * apply（行の作成）と update（金額の打ち替え）は別リクエストなので、
 * **後半だけ失敗するとマスタ単価のままの行が残る**。この状態を黙って通すと
 * 誤った金額がそのまま請求書へ投影されるため、専用メッセージで必ず知らせる。
 * 行自体は作成されているので、キャッシュの再取得は成否によらず行う。
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workItemMasterApi } from "../../api/workItemMasterApi.js";
import { workItemsApi } from "../../api/workItemsApi.js";
import { toast } from "../Toast.js";

/** 行は作成できたが、金額の打ち替えだけ失敗したことを表す */
class ApplyAmountError extends Error {
  constructor() {
    super("行は追加されましたが、金額の変更を保存できませんでした。追加された行の金額を確認してください");
    this.name = "ApplyAmountError";
  }
}

export type ApplyMasterVars = {
  masterId: string;
  /** 単一明細マスタで金額（行の合計額）を打ち替えたときのみ指定 */
  amountOverride?: number;
  /** マスタ明細の数量。打ち替え時も潰さずに保つ（謄本3通などが 1 通に化けるのを防ぐ） */
  quantity?: number;
};

export function useApplyMaster(caseId: string, onDone?: () => void) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ masterId, amountOverride, quantity }: ApplyMasterVars) => {
      const res  = await workItemMasterApi.apply(masterId, caseId);
      const rows = (res.data ?? []) as Array<{ id?: string }>;
      const createdId = rows.length === 1 ? rows[0]?.id : undefined;
      if (amountOverride !== undefined && createdId) {
        // 入力欄の金額は「行の合計額」。数量は据え置き、単価はそこから割り戻す。
        // 端数が出ても amount を明示指定するので合計額は入力どおりになる
        // （API は patch.amount があればそれを優先する。routes/work-items.ts の amount 再計算）
        const qty = quantity && quantity > 0 ? quantity : 1;
        try {
          await workItemsApi.update(caseId, createdId, {
            quantity:   qty,
            unit_price: Math.floor(amountOverride / qty),
            amount:     amountOverride,
          });
        } catch {
          throw new ApplyAmountError();
        }
      }
      return res;
    },
    // 行は作成済みなので、金額更新の成否によらず一覧を取り直す
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["case", caseId, "work-items"] });
      qc.invalidateQueries({ queryKey: ["case", caseId, "work-items", "summary"] });
      qc.invalidateQueries({ queryKey: ["work-item-master"] });
    },
    onSuccess: () => { onDone?.(); },
    onError: (e: Error) => toast(e.message, "error"),
  });
}
